import { useState, useCallback } from 'preact/hooks';
import type { ProjectSpec, ProjectSpecFeature, I18nStrings } from '@bitio-builder/shared';

interface SpecConfirmationProps {
  projectSpec: ProjectSpec | null;
  conversationSummary: string | null;
  strings: I18nStrings;
  onConfirm: (editedSpec: ProjectSpec) => void;
  onContinue: () => void;
}

const containerStyle: Record<string, string | number> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle: Record<string, string | number> = {
  padding: '20px 24px',
  borderBottom: '1px solid var(--bb-border, #E5E7EB)',
  textAlign: 'center',
};

const titleStyle: Record<string, string | number> = {
  fontSize: '18px',
  fontWeight: 600,
  marginBottom: '8px',
  color: 'var(--bb-foreground, #1A1C17)',
};

const subtitleStyle: Record<string, string | number> = {
  fontSize: '14px',
  color: 'var(--bb-muted, #6B7280)',
};

const contentStyle: Record<string, string | number> = {
  flex: 1,
  overflow: 'auto',
  padding: '20px 24px',
};

const sectionStyle: Record<string, string | number> = {
  marginBottom: '20px',
};

const sectionTitleStyle: Record<string, string | number> = {
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--bb-muted, #6B7280)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '8px',
};

const editableInputStyle: Record<string, string | number> = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '14px',
  border: '1px solid var(--bb-border, #E5E7EB)',
  borderRadius: '6px',
  background: 'white',
  color: 'var(--bb-foreground, #1A1C17)',
  lineHeight: 1.5,
  outline: 'none',
};

const editableTextareaStyle: Record<string, string | number> = {
  ...editableInputStyle,
  minHeight: '60px',
  resize: 'vertical',
  fontFamily: 'inherit',
};

const featureListStyle: Record<string, string | number> = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const featureItemStyle = (isDragging: boolean, isDragOver: boolean): Record<string, string | number> => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  padding: '12px',
  marginBottom: '8px',
  background: isDragOver ? 'var(--bb-accent, #FA9EAF)' : 'white',
  border: '1px solid var(--bb-border, #E5E7EB)',
  borderRadius: '8px',
  cursor: 'grab',
  opacity: isDragging ? 0.5 : 1,
  transition: 'background 150ms ease',
});

const dragHandleStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--bb-muted, #6B7280)',
  cursor: 'grab',
  padding: '4px',
};

const featurePriorityStyle: Record<string, string | number> = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  background: 'var(--bb-primary, #4D5D32)',
  color: 'white',
  fontSize: '12px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const featureContentStyle: Record<string, string | number> = {
  flex: 1,
};

const featureNameInputStyle: Record<string, string | number> = {
  width: '100%',
  padding: '6px 8px',
  fontSize: '14px',
  fontWeight: 500,
  border: '1px solid transparent',
  borderRadius: '4px',
  background: 'transparent',
  marginBottom: '4px',
};

const featureDescInputStyle: Record<string, string | number> = {
  width: '100%',
  padding: '4px 8px',
  fontSize: '13px',
  color: 'var(--bb-muted, #6B7280)',
  border: '1px solid transparent',
  borderRadius: '4px',
  background: 'transparent',
};

const footerStyle: Record<string, string | number> = {
  padding: '16px 24px',
  borderTop: '1px solid var(--bb-border, #E5E7EB)',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
};

const primaryButtonStyle: Record<string, string | number> = {
  width: '100%',
  padding: '14px 20px',
  background: 'var(--bb-primary, #4D5D32)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const secondaryButtonStyle: Record<string, string | number> = {
  width: '100%',
  padding: '12px 20px',
  background: 'transparent',
  color: 'var(--bb-muted, #6B7280)',
  border: '1px solid var(--bb-border, #E5E7EB)',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
};

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '20px', height: '20px', flexShrink: 0, marginRight: '8px' }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const DragIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
    <circle cx="9" cy="6" r="2" />
    <circle cx="15" cy="6" r="2" />
    <circle cx="9" cy="12" r="2" />
    <circle cx="15" cy="12" r="2" />
    <circle cx="9" cy="18" r="2" />
    <circle cx="15" cy="18" r="2" />
  </svg>
);

export function SpecConfirmation({
  projectSpec,
  conversationSummary,
  strings,
  onConfirm,
  onContinue,
}: SpecConfirmationProps) {
  // Local editable state
  const [projectName, setProjectName] = useState(projectSpec?.projectName || '');
  const [targetAudience, setTargetAudience] = useState(projectSpec?.targetAudience || '');
  const [features, setFeatures] = useState<ProjectSpecFeature[]>(
    // Ensure unique priorities by using index
    (projectSpec?.features || []).map((f, i) => ({ ...f, priority: i + 1 }))
  );
  const [technicalNotes, setTechnicalNotes] = useState(projectSpec?.technicalNotes || '');
  const [budgetIndication, setBudgetIndication] = useState(projectSpec?.budgetIndication || '');
  const [timelineIndication, setTimelineIndication] = useState(projectSpec?.timelineIndication || '');

  // Drag state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  const handleDragOver = useCallback((e: DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  }, [draggedIndex]);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e: DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setFeatures(prev => {
      const newFeatures = [...prev];
      const [draggedItem] = newFeatures.splice(draggedIndex, 1);
      newFeatures.splice(dropIndex, 0, draggedItem);
      // Reassign priorities based on new order
      return newFeatures.map((f, i) => ({ ...f, priority: i + 1 }));
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const updateFeatureName = useCallback((index: number, name: string) => {
    setFeatures(prev => prev.map((f, i) => i === index ? { ...f, name } : f));
  }, []);

  const updateFeatureDesc = useCallback((index: number, description: string) => {
    setFeatures(prev => prev.map((f, i) => i === index ? { ...f, description } : f));
  }, []);

  const handleConfirm = useCallback(() => {
    const editedSpec: ProjectSpec = {
      ...projectSpec,
      projectName: projectName || undefined,
      targetAudience: targetAudience || undefined,
      features,
      attachments: projectSpec?.attachments || [],
      technicalNotes: technicalNotes || undefined,
      budgetIndication: budgetIndication || undefined,
      timelineIndication: timelineIndication || undefined,
    };
    onConfirm(editedSpec);
  }, [projectSpec, projectName, targetAudience, features, technicalNotes, budgetIndication, timelineIndication, onConfirm]);

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={titleStyle}>{strings.projectSpec.title}</h2>
        <p style={subtitleStyle}>
          {strings.specConfirmation.subtitle}
        </p>
      </div>

      <div style={contentStyle}>
        {conversationSummary && (
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>{strings.specConfirmation.summary}</div>
            <div style={{ fontSize: '14px', color: 'var(--bb-foreground, #1A1C17)', lineHeight: 1.5 }}>
              {conversationSummary}
            </div>
          </div>
        )}

        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>{strings.projectSpec.projectName}</div>
          <input
            type="text"
            style={editableInputStyle}
            value={projectName}
            onInput={(e) => setProjectName((e.target as HTMLInputElement).value)}
            placeholder="Projektnamn..."
          />
        </div>

        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>{strings.projectSpec.targetAudience}</div>
          <input
            type="text"
            style={editableInputStyle}
            value={targetAudience}
            onInput={(e) => setTargetAudience((e.target as HTMLInputElement).value)}
            placeholder="Målgrupp..."
          />
        </div>

        {features.length > 0 && (
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>{strings.projectSpec.features}</div>
            <p style={{ fontSize: '12px', color: 'var(--bb-muted, #6B7280)', marginBottom: '12px' }}>
              Dra för att ändra prioritetsordning
            </p>
            <ul style={featureListStyle}>
              {features.map((feature, index) => (
                <li
                  key={index}
                  style={featureItemStyle(draggedIndex === index, dragOverIndex === index)}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e as DragEvent, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e as DragEvent, index)}
                  onDragEnd={handleDragEnd}
                >
                  <div style={dragHandleStyle}>
                    <DragIcon />
                  </div>
                  <div style={featurePriorityStyle}>{feature.priority}</div>
                  <div style={featureContentStyle}>
                    <input
                      type="text"
                      style={featureNameInputStyle}
                      value={feature.name}
                      onInput={(e) => updateFeatureName(index, (e.target as HTMLInputElement).value)}
                      onFocus={(e) => {
                        (e.target as HTMLInputElement).style.borderColor = 'var(--bb-border, #E5E7EB)';
                        (e.target as HTMLInputElement).style.background = 'white';
                      }}
                      onBlur={(e) => {
                        (e.target as HTMLInputElement).style.borderColor = 'transparent';
                        (e.target as HTMLInputElement).style.background = 'transparent';
                      }}
                    />
                    <input
                      type="text"
                      style={featureDescInputStyle}
                      value={feature.description || ''}
                      onInput={(e) => updateFeatureDesc(index, (e.target as HTMLInputElement).value)}
                      placeholder="Beskrivning..."
                      onFocus={(e) => {
                        (e.target as HTMLInputElement).style.borderColor = 'var(--bb-border, #E5E7EB)';
                        (e.target as HTMLInputElement).style.background = 'white';
                      }}
                      onBlur={(e) => {
                        (e.target as HTMLInputElement).style.borderColor = 'transparent';
                        (e.target as HTMLInputElement).style.background = 'transparent';
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>{strings.projectSpec.technicalNotes}</div>
          <textarea
            style={editableTextareaStyle}
            value={technicalNotes}
            onInput={(e) => setTechnicalNotes((e.target as HTMLTextAreaElement).value)}
            placeholder="Tekniska anteckningar..."
          />
        </div>

        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>{strings.projectSpec.budget}</div>
          <input
            type="text"
            style={editableInputStyle}
            value={budgetIndication}
            onInput={(e) => setBudgetIndication((e.target as HTMLInputElement).value)}
            placeholder="Budgetindikation..."
          />
        </div>

        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>{strings.projectSpec.timeline}</div>
          <input
            type="text"
            style={editableInputStyle}
            value={timelineIndication}
            onInput={(e) => setTimelineIndication((e.target as HTMLInputElement).value)}
            placeholder="Tidsram..."
          />
        </div>
      </div>

      <div style={footerStyle}>
        <button style={primaryButtonStyle} onClick={handleConfirm}>
          <CheckIcon />
          {strings.specConfirmation.confirm}
        </button>
        <button style={secondaryButtonStyle} onClick={onContinue}>
          {strings.specConfirmation.continue}
        </button>
      </div>
    </div>
  );
}
